import React, { useState, useEffect } from 'react';
import { Card, Spinner, Badge, Form } from 'react-bootstrap';
import { getSystemSubscriptionAnalytics } from '../../api/api';

const MODULE_COLORS = {
  examination: '#6366f1',
  teaching_copilot: '#0ea5e9',
  AskOS: '#f59e0b',
  content_generation: '#10b981',
  assessment_bank: '#ef4444',
};

const MODULE_SHORT = {
  examination: 'ExamOS',
  teaching_copilot: 'Copilot',
  AskOS: 'AskOS',
  content_generation: 'ContentGen',
  assessment_bank: 'AssessBank',
};

function Pill({ mod }) {
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 10, marginRight: 3, marginBottom: 3,
      background: MODULE_COLORS[mod] + '22',
      color: MODULE_COLORS[mod],
      border: `1px solid ${MODULE_COLORS[mod]}55`,
      display: 'inline-block',
    }}>
      {MODULE_SHORT[mod] || mod}
    </span>
  );
}

function BarCell({ value, max, color = '#6366f1' }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, height: 8, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 4, transition: 'width .4s' }} />
      </div>
      <span style={{ fontSize: 11, fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>
        ${value.toFixed(4)}
      </span>
    </div>
  );
}

export default function SubscriptionAnalyticsPanel() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('total_spent');

  useEffect(() => {
    getSystemSubscriptionAnalytics()
      .then(setData)
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, []);

  const totalSpent = data.reduce((s, r) => s + r.total_spent, 0);
  const totalBalance = data.reduce((s, r) => s + r.wallet_balance, 0);
  const totalReqs = data.reduce((s, r) => s + r.request_count, 0);
  const maxSpent = Math.max(...data.map((r) => r.total_spent), 0.001);

  const filtered = data
    .filter((r) => !search || r.tenant_name.toLowerCase().includes(search.toLowerCase()) || r.tenant_slug.includes(search.toLowerCase()))
    .sort((a, b) => b[sortBy] - a[sortBy]);

  return (
    <div>
      {/* Summary KPI cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Total Spent (All Tenants)', value: `$${totalSpent.toFixed(4)}`, color: '#6366f1' },
          { label: 'Total Wallet Balance', value: `$${totalBalance.toFixed(2)}`, color: '#10b981' },
          { label: 'Total AI Requests', value: totalReqs.toLocaleString(), color: '#0ea5e9' },
          { label: 'Active Tenants', value: data.filter((r) => r.status === 'active').length, color: '#f59e0b' },
        ].map((s) => (
          <Card key={s.label} className="border-0 shadow-sm rounded-4 text-center p-3">
            <div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 3 }}>{s.label}</div>
          </Card>
        ))}
      </div>

      {/* Controls */}
      <div className="d-flex gap-2 mb-3 align-items-center flex-wrap">
        <Form.Control
          size="sm"
          placeholder="Search tenant…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 220 }}
        />
        <Form.Select size="sm" value={sortBy} onChange={(e) => setSortBy(e.target.value)} style={{ maxWidth: 180 }}>
          <option value="total_spent">Sort: Credits Spent</option>
          <option value="wallet_balance">Sort: Wallet Balance</option>
          <option value="request_count">Sort: API Requests</option>
        </Form.Select>
        {loading && <Spinner size="sm" animation="border" className="text-primary" />}
      </div>

      {/* Table */}
      <div style={{ overflowX: 'auto' }}>
        <table className="table table-hover table-sm align-middle small mb-0">
          <thead className="table-light">
            <tr>
              <th>Tenant</th>
              <th>Status</th>
              <th>Subscribed Modules</th>
              <th style={{ minWidth: 180 }}>Credits Spent</th>
              <th>Wallet Balance</th>
              <th>AI Requests</th>
              <th>Top Operation</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && !loading ? (
              <tr><td colSpan={7} className="text-center text-muted py-4">No data found.</td></tr>
            ) : (
              filtered.map((r) => (
                <tr key={r.tenant_id}>
                  <td>
                    <div className="fw-bold">{r.tenant_name}</div>
                    <div className="text-muted" style={{ fontSize: 10 }}>{r.tenant_slug}</div>
                  </td>
                  <td>
                    <Badge bg={r.status === 'active' ? 'success' : r.status === 'trial' ? 'warning' : 'secondary'}
                      className="text-capitalize">{r.status}</Badge>
                  </td>
                  <td style={{ maxWidth: 220 }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                      {(r.modules || []).map((m) => <Pill key={m} mod={m} />)}
                    </div>
                  </td>
                  <td style={{ minWidth: 180 }}>
                    <BarCell value={r.total_spent} max={maxSpent} color="#6366f1" />
                  </td>
                  <td>
                    <span style={{ fontWeight: 700, color: r.wallet_balance < 10 ? '#ef4444' : '#10b981' }}>
                      ${r.wallet_balance.toFixed(2)}
                    </span>
                  </td>
                  <td className="text-center">{r.request_count.toLocaleString()}</td>
                  <td>
                    {r.top_operation ? (
                      <span className="badge bg-light text-dark border" style={{ fontSize: 10, fontWeight: 600 }}>
                        {r.top_operation.replace(/_/g, ' ')}
                      </span>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
